import React, { Fragment } from "react";
import { Dialog as HeadlessDialog, Transition } from "@headlessui/react";
import { cn } from "@/lib/utils";
import { Button, ButtonProps } from "./button";
import {
	XIcon,
	AlertTriangleIcon,
	CheckCircleIcon,
	InfoIcon,
} from "lucide-react";

// Dialog Root Component
export interface DialogProps {
	open: boolean;
	onClose: (value: boolean) => void;
	children: React.ReactNode;
	initialFocus?: React.MutableRefObject<HTMLElement | null>;
	className?: string;
	variant?: "light" | "dark";
	backdropClassName?: string;
}

export const Dialog = React.forwardRef<HTMLDivElement, DialogProps>(
	function Dialog({
		open,
		onClose,
		children,
		initialFocus,
		className,
		backdropClassName,
	}) {
		return (
			<Transition show={open} as={Fragment}>
				<HeadlessDialog
					as="div"
					className={cn("relative z-50 focus:outline-none", className)}
					onClose={onClose}
					initialFocus={initialFocus}
				>
					{/* Backdrop */}
					<Transition.Child
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<div
							className={cn(
								"fixed inset-0 z-10 w-screen bg-gray-900/50 backdrop-blur-sm transition-opacity data-closed:opacity-0",
								backdropClassName,
							)}
						/>
					</Transition.Child>

					{/* Dialog positioning */}
					<div className="fixed inset-0 z-10 w-screen overflow-y-auto">
						<div className="flex min-h-full items-center justify-center p-4 text-center focus:outline-none">
							<Transition.Child
								as={Fragment}
								enter="ease-out duration-300"
								enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
								enterTo="opacity-100 translate-y-0 sm:scale-100"
								leave="ease-in duration-200"
								leaveFrom="opacity-100 translate-y-0 sm:scale-100"
								leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
							>
								{children}
							</Transition.Child>
						</div>
					</div>
				</HeadlessDialog>
			</Transition>
		);
	},
);

// Dialog Panel Component
export interface DialogPanelProps {
	children: React.ReactNode;
	className?: string;
	variant?: "light" | "dark";
}

export const DialogPanel = React.forwardRef<HTMLDivElement, DialogPanelProps>(
	function DialogPanel({ children, className, variant = "light" }, ref) {
		return (
			<HeadlessDialog.Panel
				ref={ref}
				className={cn(
					"relative transform overflow-hidden rounded-lg shadow-xl outline -outline-offset-1 transition-all w-full max-w-md",
					variant === "light"
						? "bg-white outline-gray-200/10"
						: "bg-gray-800 outline-white/10 text-white",
					className,
				)}
			>
				{children}
			</HeadlessDialog.Panel>
		);
	},
);

// Dialog Title Component
export interface DialogTitleProps {
	children: React.ReactNode;
	className?: string;
	variant?: "light" | "dark";
}

export const DialogTitle = React.forwardRef<
	HTMLHeadingElement,
	DialogTitleProps
>(function DialogTitle({ children, className, variant = "light" }, ref) {
	return (
		<HeadlessDialog.Title
			ref={ref}
			as="h3"
			className={cn(
				"text-base font-semibold",
				variant === "light" ? "text-gray-900" : "text-white",
				className,
			)}
		>
			{children}
		</HeadlessDialog.Title>
	);
});

// Dialog Description Component
export interface DialogDescriptionProps {
	children: React.ReactNode;
	className?: string;
	variant?: "light" | "dark";
}

export const DialogDescription = React.forwardRef<
	HTMLParagraphElement,
	DialogDescriptionProps
>(function DialogDescription({ children, className, variant = "light" }, ref) {
	return (
		<HeadlessDialog.Description
			ref={ref}
			className={cn(
				"mt-2 text-sm",
				variant === "light" ? "text-gray-600" : "text-gray-400",
				className,
			)}
		>
			{children}
		</HeadlessDialog.Description>
	);
});

// Dialog Icon Component
export interface DialogIconProps {
	type?: "warning" | "success" | "info" | "error";
	className?: string;
	variant?: "light" | "dark";
}

export const DialogIcon = React.forwardRef<HTMLDivElement, DialogIconProps>(
	function DialogIcon({ type = "warning", className, variant = "light" }, ref) {
		const iconColors = {
			warning: {
				bg: variant === "light" ? "bg-amber-100" : "bg-amber-500/10",
				icon: variant === "light" ? "text-amber-600" : "text-amber-400",
			},
			success: {
				bg: variant === "light" ? "bg-green-100" : "bg-green-500/10",
				icon: variant === "light" ? "text-green-600" : "text-green-400",
			},
			info: {
				bg: variant === "light" ? "bg-blue-100" : "bg-blue-500/10",
				icon: variant === "light" ? "text-blue-600" : "text-blue-400",
			},
			error: {
				bg: variant === "light" ? "bg-red-100" : "bg-red-500/10",
				icon: variant === "light" ? "text-red-600" : "text-red-400",
			},
		};

		const IconComponent = {
			warning: AlertTriangleIcon,
			success: CheckCircleIcon,
			info: InfoIcon,
			error: AlertTriangleIcon,
		}[type];

		return (
			<div
				ref={ref}
				className={cn(
					"mx-auto flex size-12 shrink-0 items-center justify-center rounded-full sm:mx-0 sm:size-10",
					iconColors[type].bg,
					className,
				)}
			>
				<IconComponent className={cn("size-6", iconColors[type].icon)} />
			</div>
		);
	},
);

// Dialog Header Component
export interface DialogHeaderProps {
	children: React.ReactNode;
	className?: string;
	showCloseButton?: boolean;
	onClose?: () => void;
	icon?: "warning" | "success" | "info" | "error";
	variant?: "light" | "dark";
}

export const DialogHeader = React.forwardRef<HTMLDivElement, DialogHeaderProps>(
	function DialogHeader(
		{
			children,
			className,
			showCloseButton = true,
			onClose,
			icon,
			variant = "light",
		},
		ref,
	) {
		return (
			<div ref={ref} className={cn("px-4 pt-5 pb-4 sm:p-6 sm:pb-4", className)}>
				<div className="sm:flex sm:items-start">
					{icon && <DialogIcon type={icon} variant={variant} />}
					<div
						className={cn(
							icon ? "mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left" : "",
							"flex-1",
						)}
					>
						{children}
					</div>
					{showCloseButton && onClose && (
						<button
							onClick={onClose}
							className={cn(
								"rounded-full p-1 transition-colors absolute top-4 right-4",
								variant === "light"
									? "hover:bg-gray-100 text-gray-500"
									: "hover:bg-gray-700 text-gray-400",
							)}
							aria-label="Close dialog"
						>
							<XIcon size={18} />
						</button>
					)}
				</div>
			</div>
		);
	},
);

// Dialog Body Component
export interface DialogBodyProps {
	children: React.ReactNode;
	className?: string;
	variant?: "light" | "dark";
}

export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
	function DialogBody({ children, className }, ref) {
		return (
			<div ref={ref} className={cn("px-4 py-2 sm:px-6", className)}>
				{children}
			</div>
		);
	},
);

// Dialog Footer Component
export interface DialogFooterProps {
	children: React.ReactNode;
	className?: string;
	direction?: "row" | "column";
	variant?: "light" | "dark";
}

export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
	function DialogFooter(
		{ children, className, direction = "row", variant = "light" },
		ref,
	) {
		return (
			<div
				ref={ref}
				className={cn(
					"px-4 py-3 sm:px-6",
					variant === "light" ? "bg-gray-50" : "bg-gray-700/25",
					className,
				)}
			>
				{direction === "row" ? (
					<div className="sm:flex sm:flex-row-reverse">{children}</div>
				) : (
					<div className="flex flex-col gap-3">{children}</div>
				)}
			</div>
		);
	},
);

// Dialog Close Button Component
export interface DialogCloseButtonProps extends Omit<ButtonProps, "variant"> {
	children?: React.ReactNode;
	themeVariant?: "light" | "dark";
	buttonVariant?: ButtonProps["variant"];
}

export function DialogCloseButton({
	children = "Cancel",
	themeVariant = "light",
	buttonVariant,
	size = "md",
	...props
}: DialogCloseButtonProps) {
	// Default to outline for light theme, and a dark-mode appropriate style for dark theme
	const variant =
		buttonVariant || (themeVariant === "light" ? "outline" : "ghost");

	return (
		<Button
			variant={variant}
			size={size}
			className={cn(
				"mt-3 inline-flex w-full justify-center rounded-md sm:mt-0 sm:w-auto",
				themeVariant === "dark" && "bg-white/10 text-white hover:bg-white/20",
				props.className,
			)}
			{...props}
		>
			{children}
		</Button>
	);
}

// Dialog Action Button Component
export interface DialogActionButtonProps extends Omit<ButtonProps, "variant"> {
	children: React.ReactNode;
	themeVariant?: "light" | "dark";
	buttonVariant?: ButtonProps["variant"];
}

export function DialogActionButton({
	children,
	buttonVariant = "primary",
	size = "md",
	...props
}: DialogActionButtonProps) {
	return (
		<Button
			variant={buttonVariant}
			size={size}
			className={cn(
				"inline-flex w-full justify-center rounded-md sm:ml-3 sm:w-auto",
				props.className,
			)}
			{...props}
		>
			{children}
		</Button>
	);
}

/**
 * Example usage:
 *
 * ```tsx
 * import { useState } from "react";
 * import { Button } from "@/design-elements/button";
 * import {
 *   Dialog,
 *   DialogPanel,
 *   DialogHeader,
 *   DialogTitle,
 *   DialogDescription,
 *   DialogBody,
 *   DialogFooter,
 *   DialogCloseButton,
 *   DialogActionButton,
 *   DialogIcon
 * } from "@/design-elements/dialog";
 *
 * function MyDialog() {
 *   const [open, setOpen] = useState(false);
 *
 *   return (
 *     <>
 *       <Button onClick={() => setOpen(true)}>Open Dialog</Button>
 *
 *       <Dialog open={open} onClose={setOpen}>
 *         <DialogPanel>
 *           <DialogHeader onClose={() => setOpen(false)}>
 *             <DialogTitle>Payment Successful</DialogTitle>
 *             <DialogDescription>
 *               Your payment has been successfully submitted.
 *             </DialogDescription>
 *           </DialogHeader>
 *
 *           <DialogBody>
 *             <div className="bg-gray-50 p-4 rounded-md">
 *               <p className="text-sm">Order #12345</p>
 *             </div>
 *           </DialogBody>
 *
 *           <DialogFooter>
 *             <DialogCloseButton onClick={() => setOpen(false)}>
 *               Cancel
 *             </DialogCloseButton>
 *             <DialogActionButton onClick={() => console.log("Action taken")}>
 *               Got it, thanks!
 *             </DialogActionButton>
 *           </DialogFooter>
 *         </DialogPanel>
 *       </Dialog>
 *     </>
 *   );
 * }
 * ```
 */
